import { useEffect, useState, useMemo } from "react";
import FeedbackComment from "pages/conductLegalResearch/fragments/feedBackComment";
import ResponseInput from "pages/conductLegalResearch/fragments/responseInput";
import SectionHeader from "pages/conductLegalResearch/fragments/sectionHeader";
import { formatDate } from "utils/helpers";
import ShowUI, { LEGISLATIVE_DRAFTING_UI_RULES } from "utils/authRules";
import DocumentViewerModal from "components/DocumentViewer";
import DynamicFormInput from "components/form/form";
import FileUpload from "components/FileUpload";
import { updateAuditLawImplementation } from "services/lawImplementationService";
import { useToast } from "contexts/ToastContext";
import { LoadingIcon } from "utils/constant";
import { LAW_IMPLEMENTATION_REQUEST_STATUS } from "utils/constantValues";
import { useTranslation } from "react-i18next";
import { useAuth } from "contexts/AuthContext";
import EvaluationModal from "components/EvaluationModal";
import { lawImplementationEvaluationApi } from "services/directiveEvaluationService";

interface Feedback {
    id: number;
    research_request: number;
    given_by: string;
    role: string;
    feedback_type: string;
    note: string;
    status: string;
    // add any other fields your API returns
}

export default function MainRightPanel({ request, type }: { request: any, type?: string }) {
    const { t } = useTranslation();
    const [letterRefNum, setLetterRefNum] = useState("");
    const [completionLetter, setCompletionLetter] = useState<File | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const { showToast } = useToast();
    const [formState, setFormState] = useState({ ...request });
    const [feedbacks, setFeedbacks] = useState<Feedback[]>(formState?.feedbacks || request.audit_feedbacks || []);
    const [respondbackFeedbacks, setRespondbackFeedback] = useState<Feedback[]>(request?.audit_respond_back_feedbacks || []);
    const [state, setState] = useState("feedback");
    const contextData = useAuth();
    const [showEvaluation, setShowEvaluation] = useState(false);

    const teamMembers = Object.values(
        (request?.teams || [])
            .flatMap((tm: any) => tm?.prosecutors || [])
            .reduce((acc: Record<string, any>, p: any) => {
                const key = String(p?.user_id ?? p?.id ?? "");
                if (key && !acc[key]) acc[key] = p;
                return acc;
            }, {})
    );
    const canEvaluate =
        contextData?.user?.user_type !== "external" &&
        teamMembers.length > 0 &&
        contextData?.user?.roles?.some(
            (role: string) =>
                role === "LegalResearchCoordinator" || role === "LRDIFUDDirector"
        );
    const evaluationBearer =
        contextData.token || contextData?.user?.access_token || "";
    const evaluatedBy =
        contextData?.user?.full_name ||
        contextData?.user?.first_name ||
        contextData?.user?.user_id;

    const auditFindingsDocs = useMemo(() => {
        const legacy = request?.file_histories
            ?.filter((f: any) => f.file_type === "audit_findings")
            .map((f: any) => ({
                uu_id: f.id?.toString() || "1",
                document_type: "audit_findings",
                document_uuid: f.id?.toString() || "1",
                document_path: f.audit_findings_file_details,
                created_at: f.created_at || new Date().toISOString(),
                updated_at: f.updated_at || new Date().toISOString(),
            })) || [];

        const documentFiles = request?.documents
            ?.filter((d: any) => d.document_type === "audit_findings_files")
            .map((d: any) => ({
                uu_id: d.uu_id,
                document_type: d.document_type,
                document_uuid: d.document_uuid,
                document_path: d.document_path,
                created_at: d.created_at,
                updated_at: d.updated_at,
            })) || [];

        const allDocs = [...legacy, ...documentFiles];
        return allDocs.sort(
            (a: any, b: any) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );
    }, [request]);

    const handleSave = async () => {
        setSubmitting(true);
        const data = new FormData();
        data.append("current_approval_status", LAW_IMPLEMENTATION_REQUEST_STATUS.letter_refnum_submitted.value);
        // Store the registrar reference number in adminn_unit_id so it does not override
        // the application number held in registry_number.
        data.append("adminn_unit_id", letterRefNum);
        if (completionLetter) {
            data.append("audit_files", completionLetter);
        }

        try {
            const response = await updateAuditLawImplementation(request.id, data);
            setFormState((prev: any) => ({ ...prev, ...response }));
            showToast(t("ArchiveDetailsSubmittedSuccess") || "Archive details submitted successfully!", "success");
        } catch (error) {
            console.error("Error archiving request:", error);
            showToast(t("FailedToArchiveRequest") || "Failed to archive request.", "error");
        } finally {
            setSubmitting(false);
        }
    };

    useEffect(() => { }, [formState])
    
    return (
        <div className="overflow-y-scroll scrollbar-hide flex w-[45%] flex-col bg-white rounded-lg shadow-md p-6">
            <ShowUI
                ui_key={LEGISLATIVE_DRAFTING_UI_RULES.edit_reference_number.value}
                currentStatus={request.current_approval_status}
                forwardStatus={request.forward_status}
            >
                <div className="mb-8 p-4 border-2 border-dashed border-gray-200 rounded-xl bg-gray-50">
                    <h3 className="text-lg font-bold text-gray-800 mb-4">{t("RegistrarArchiveSection") || "Registrar Archive Section"}</h3>
                    <DynamicFormInput
                        inputs={[{
                            value: letterRefNum,
                            type: "text",
                            label: t("LetterReferenceNumber") || "Letter Reference Number",
                            name: "letter_reference_number",
                            onChange: (e: any) => setLetterRefNum(e.target.value),
                            placeholder: t("LetterReferenceNumberPlaceholder") || "Eg. 12345",
                        }]}
                    />
                    {auditFindingsDocs.length > 0 && (
                        <div className="mb-4">
                            <p className="text-sm font-semibold text-gray-700 mb-2">{t("AuditFindingsFiles") || "Audit Findings Files"}</p>
                            <DocumentViewerModal
                                documents={auditFindingsDocs}
                                baseUrl={`${process.env.REACT_APP_API_BASE_URL}law_implementation_follow_up/audit-follow-up-documents/document`}
                            />
                        </div>
                    )}
                    <div className="mb-6">
                        <label className="block text-sm font-medium text-gray-700 mb-2">{t("CompletionLetter") || "Completion Letter"}</label>
                        <FileUpload onChange={setCompletionLetter} />
                    </div>
                    <button
                        onClick={handleSave}
                        disabled={submitting || !letterRefNum}
                        className="w-full bg-black text-white font-bold py-3 rounded-lg hover:bg-gray-800 disabled:bg-gray-400 transition-colors"
                    >
                        {submitting ? <div className="flex items-center justify-center">{LoadingIcon} {t("Submitting") || "Submitting..."}</div> : (t("SaveAndArchive") || "Save & Archive")}
                    </button>
                </div>
            </ShowUI>

            <div className="flex gap-4 items-center pb-2 mb-4 border-b-2">
                <p onClick={() => setState("feedback")} className={`cursor-pointer font-semibold ${state === "feedback" ? "text-base text-white bg-black p-2 rounded-lg" : "text-base text-gray-400"}`}>
                    {t("FeedbackSection") || "Feedback Section"}
                </p>
                <p onClick={() => setState("respondback")} className={`cursor-pointer font-semibold ${state === "respondback" ? "text-base text-white bg-black p-2 rounded-lg" : "text-base text-gray-400"}`}>
                    {t("RespondBackSection") || "Respond Back Section"}
                </p>
                {canEvaluate && (
                    <button
                        onClick={() => setShowEvaluation(true)}
                        className="ml-auto flex items-center gap-1.5 text-sm font-semibold text-[#9B0505] border border-[#9B0505] rounded-lg px-3 py-1.5 hover:bg-[#9B0505] hover:text-white transition"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-6m4 6V7m4 10v-3M5 21h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v14a2 2 0 002 2z" />
                        </svg>
                        {t("EvaluateTeam") || "Evaluate Team"}
                    </button>
                )}
            </div>
            {showEvaluation && (
                <EvaluationModal
                    requestId={request.id}
                    teamMembers={teamMembers}
                    evaluatedBy={evaluatedBy}
                    bearer={evaluationBearer}
                    api={lawImplementationEvaluationApi}
                    onClose={() => setShowEvaluation(false)}
                />
            )}
            {/* Feedback Comments */}
            <div className="overflow-y-scroll scrollbar-hide flex-1 space-y-6 mb-6">
                {state === "feedback" ? (
                    <>
                        {feedbacks.map((feedback: any) => (
                            <FeedbackComment
                                key={feedback.id}
                                name={feedback.given_by}
                                role={feedback.role}
                                commentedDate={formatDate(feedback.created_at)}
                                comment={feedback.summary}
                            />
                        ))}
                        {feedbacks.length === 0 && (
                            <p className="text-center my-auto">{t("NoMessagesYet") || "No Messages Yet"}</p>
                        )}
                    </>
                ) : (
                    <>
                        {respondbackFeedbacks?.slice().reverse().map((feedback: any) => (
                            feedback.feedback_type !== "stakeholder_request" &&
                            <FeedbackComment
                                key={feedback.id}
                                name={feedback.given_by}
                                role={feedback.role}
                                commentedDate={formatDate(feedback.created_at)}
                                comment={feedback.note}
                                file={feedback.documents || feedback.file}
                                file_type="followup_respond_back_feedback_files"
                                file_url="law_implementation_follow_up/audit-follow-up-documents/document/"
                                status={feedback.feedback_type}
                            />
                        ))}
                        {respondbackFeedbacks.length === 0 && (
                            <p className="text-center my-auto">{t("NoRespondBackFeedbackYet") || "No Respond Back Feedback Yet"}</p>
                        )}
                    </>
                )}
            </div>
            {/* Write Back Response */}
            {state === "feedback" &&
                <ResponseInput request={request} onSubmit={setFeedbacks} type={type} />}
        </div>
    );
}